// Pipeline-Logik der Leads (ERP/CRM-Plan, Abschnitt 4).
//
// Alles hier ist pur: Statusvokabular, erlaubte Uebergaenge, Priorisierung und
// der zulaessige Erstkontakt-Kanal. Damit bleibt die Fachlichkeit ohne DOM- und
// ohne Netz-Umgebung pruefbar (Standing Decision: „duenne Container, Logik in
// Hooks + pure Funktionen"). Die unreinen Teile liegen in leadService.ts.
//
// Die Statuswerte sind wortgleich mit dem CHECK in supabase/crm_leads.sql. Wer
// hier erweitert, muss dort nachziehen — sonst nimmt die Datenbank den neuen
// Wert nicht an.

import { calculateDistanceKm, Coordinates } from './nearbyProjects';

// ─── Status ──────────────────────────────────────────────────────────────────

export const LEAD_STATUSES = [
    'neu',
    'qualifiziert',
    'kontaktiert',
    'angebot',
    'gewonnen',
    'verloren',
    'gesperrt',
] as const;

export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Anzeigetexte. Record erzwingt einen Eintrag je Schluessel. */
export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
    neu: 'Neu',
    qualifiziert: 'Qualifiziert',
    kontaktiert: 'Kontaktiert',
    angebot: 'Angebot',
    gewonnen: 'Gewonnen',
    verloren: 'Verloren',
    gesperrt: 'Gesperrt',
};

/**
 * Statusfarben als HEX — EINE Quelle fuer alles, was kein Tailwind versteht.
 *
 * Die Kartenmarker sind SVG in einem Leaflet-`DivIcon`; dort laesst sich keine
 * Tailwind-Klasse verwenden, und ein zur Laufzeit zusammengesetzter
 * Klassenname („bg-${status}-400") ueberlebt Tailwinds Purge nicht. Der
 * Verlauf ist derselbe wie bei den Board-Spalten (LeadsBoard.SPALTEN_FARBE):
 * neu = blau … angebot = amber (Plan Abschnitt 6).
 */
export const LEAD_STATUS_FARBEN: Record<LeadStatus, string> = {
    neu: '#60a5fa',          // blue-400
    qualifiziert: '#38bdf8', // sky-400
    kontaktiert: '#2dd4bf',  // teal-400
    angebot: '#fbbf24',      // amber-400
    gewonnen: '#10b981',     // emerald-500
    verloren: '#cbd5e1',     // slate-300
    gesperrt: '#f87171',     // red-400
};

/** Farbe zu einem gespeicherten Wert; Unbekanntes bekommt neutrales Grau. */
export const leadStatusFarbe = (value?: string | null): string =>
    isLeadStatus(value) ? LEAD_STATUS_FARBEN[value] : '#94a3b8';

/**
 * Die Spalten des Kanban-Boards, in Pipeline-Reihenfolge. `gesperrt` fehlt
 * bewusst: ein Werbewiderspruch ist keine Vertriebsstufe, sondern ein
 * Ausschluss — er gehoert in die Liste, nicht auf das Board.
 */
export const LEAD_BOARD_COLUMNS: LeadStatus[] = [
    'neu',
    'qualifiziert',
    'kontaktiert',
    'angebot',
    'gewonnen',
    'verloren',
];

/** Status, in denen der Lead noch bearbeitet wird. */
export const LEAD_ACTIVE_STATUSES: LeadStatus[] = ['neu', 'qualifiziert', 'kontaktiert', 'angebot'];

/** Endstatus — von hier fuehrt kein Weg zurueck. */
export const LEAD_TERMINAL_STATUSES: LeadStatus[] = ['gewonnen', 'verloren', 'gesperrt'];

export const isLeadStatus = (value: unknown): value is LeadStatus =>
    typeof value === 'string' && (LEAD_STATUSES as readonly string[]).includes(value);

export const isLeadActive = (status: LeadStatus): boolean => LEAD_ACTIVE_STATUSES.includes(status);

/** Anzeigetext zu einem gespeicherten Wert; Unbekanntes wird roh durchgereicht. */
export const leadStatusLabel = (value?: string | null): string => {
    if (!value) return '—';
    return isLeadStatus(value) ? LEAD_STATUS_LABELS[value] : value;
};

// ─── Uebergaenge ─────────────────────────────────────────────────────────────

/**
 * Der lineare Pfad der Pipeline. Aus JEDEM aktiven Status sind zusaetzlich
 * `verloren` und `gesperrt` erreichbar (Absage bzw. Werbewiderspruch koennen
 * jederzeit eintreten); die drei Endstatus sind bewusst Sackgassen. Wer einen
 * verlorenen Lead erneut angehen will, legt einen neuen an — sonst zerfaellt
 * die Conversion-Kohorte (Plan 6, „Metriken formal definiert").
 */
const FORWARD_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
    neu: ['qualifiziert'],
    qualifiziert: ['kontaktiert'],
    kontaktiert: ['angebot'],
    angebot: ['gewonnen'],
    gewonnen: [],
    verloren: [],
    gesperrt: [],
};

/** Zusatzangaben, ohne die manche Uebergaenge nicht zulaessig sind. */
export interface TransitionContext {
    /**
     * Der bereits angelegte Kunde. `gewonnen` ohne ihn waere ein
     * Vertriebserfolg ohne Stammsatz — dieselbe Bedingung steht als
     * CHECK `leads_won_needs_customer` in supabase/crm_leads.sql.
     */
    customerId?: string | null;
}

/**
 * Ist der Uebergang erlaubt? Selbstuebergaenge sind es nicht (Muster
 * quoteLogic.canTransition).
 *
 * ACHTUNG: `to === 'gewonnen'` verlangt einen `customerId` im Kontext. Ohne
 * Kontext aufgerufen liefert dieser Uebergang deshalb `false` — das ist
 * beabsichtigt und die sichere Richtung: die Oberflaeche fuehrt vorher durch
 * die Konvertierung.
 */
export const canTransition = (
    from: LeadStatus,
    to: LeadStatus,
    ctx: TransitionContext = {},
): boolean => {
    if (from === to) return false;
    if (!isLeadStatus(from) || !isLeadStatus(to)) return false;

    // Abbruchwege: aus jedem AKTIVEN Status heraus jederzeit moeglich.
    if ((to === 'verloren' || to === 'gesperrt') && isLeadActive(from)) return true;

    if (!FORWARD_TRANSITIONS[from].includes(to)) return false;
    if (to === 'gewonnen' && !(ctx.customerId || '').trim()) return false;
    return true;
};

/**
 * Warum ein Uebergang nicht geht — als Satz fuer die Oberflaeche. `null`, wenn
 * er zulaessig ist. Getrennt von canTransition, damit die Pruefung selbst
 * boolesch bleibt und der Wortlaut trotzdem an EINER Stelle steht.
 */
export const transitionHindernis = (
    from: LeadStatus,
    to: LeadStatus,
    ctx: TransitionContext = {},
): string | null => {
    if (canTransition(from, to, ctx)) return null;
    if (from === to) return `Der Lead steht bereits auf „${leadStatusLabel(to)}“.`;
    if (to === 'gewonnen' && !(ctx.customerId || '').trim()) {
        return 'Erst in einen Kunden umwandeln — „Gewonnen“ ohne Kundenstammsatz ist nicht vorgesehen.';
    }
    if (LEAD_TERMINAL_STATUSES.includes(from)) {
        return `„${leadStatusLabel(from)}“ ist ein Endstatus. Für einen neuen Anlauf bitte einen neuen Lead anlegen.`;
    }
    return `Von „${leadStatusLabel(from)}“ ist „${leadStatusLabel(to)}“ nicht erreichbar.`;
};

/** Die Ziele, die von hier aus tatsaechlich anwaehlbar sind. */
export const allowedTransitions = (from: LeadStatus, ctx: TransitionContext = {}): LeadStatus[] =>
    LEAD_STATUSES.filter((to) => canTransition(from, to, ctx));

/**
 * Zeitstempel, die ein Statuswechsel setzt. Pur, damit die Zuordnung
 * „welcher Status stempelt welches Feld?" testbar bleibt; das Schreiben
 * uebernimmt leadService.setLeadStatus.
 */
export const timestampsForStatus = (
    to: LeadStatus,
    nowIso: string,
): Partial<Record<'qualifiedAt' | 'wonAt' | 'lostAt', string>> => {
    if (to === 'qualifiziert') return { qualifiedAt: nowIso };
    if (to === 'gewonnen') return { wonAt: nowIso };
    // 'gesperrt' stempelt bewusst NICHT lostAt: ein Werbewiderspruch ist kein
    // verlorener Auftrag und darf die Conversion-Quote nicht verschlechtern.
    if (to === 'verloren') return { lostAt: nowIso };
    return {};
};

// ─── Priorisierung ───────────────────────────────────────────────────────────

/** Was die Priorisierung vom Lead braucht (bewusst schmal gehalten). */
export interface LeadPriorityInput {
    score?: number | null;
    lat?: number | null;
    lng?: number | null;
    status?: LeadStatus;
}

/** Ein aktives Projekt als Bezugspunkt der Naehe-Bewertung. */
export interface ActiveProjectPoint extends Coordinates {
    id?: string;
    name?: string;
}

export interface LeadPriority {
    /** 0..100, gerundet. Hoeher = zuerst anrufen. */
    priority: number;
    /** Der eingeflossene KI-Score, oder null wenn keiner vorlag. */
    score: number | null;
    /** Entfernung zum naechsten aktiven Projekt in km, null ohne Bezugspunkt. */
    distanceKm: number | null;
    /** Der Naehe-Anteil (0..NEARBY_BONUS_MAX). */
    naeheBonus: number;
    /** Ein Satz, der die Zahl erklaert — die Oberflaeche zeigt ihn im Tooltip. */
    begruendung: string;
}

/**
 * Ohne KI-Score wird neutral gerechnet. 50 statt 0: ein unbewerteter Lead ist
 * nicht schlecht, er ist unbekannt — mit 0 raenge jeder frisch importierte
 * Datensatz ganz unten und wuerde nie angefasst.
 */
const NEUTRAL_SCORE = 50;

/** Deckel des Naehe-Zuschlags. Ein Bonus, der den Score schlaegt, waere keiner. */
export const NEARBY_BONUS_MAX = 20;

/**
 * Naehe-Stufen in km. Bewusst Stufen statt einer stetigen Funktion: der
 * Unterschied zwischen 41 und 43 km ist fuer die Disposition bedeutungslos,
 * der zwischen „gleicher Landkreis" und „andere Ecke der Republik" nicht.
 */
const NEARBY_STEPS: { maxKm: number; bonus: number; text: string }[] = [
    { maxKm: 25, bonus: 20, text: 'im direkten Umfeld eines laufenden Projekts' },
    { maxKm: 50, bonus: 12, text: 'in Reichweite eines laufenden Projekts' },
    { maxKm: 100, bonus: 5, text: 'in der weiteren Region eines laufenden Projekts' },
];

const clamp100 = (n: number): number => Math.max(0, Math.min(100, n));

/**
 * Zahl aus einem Rohwert — oder null.
 *
 * null/undefined/'' MUESSEN vor Number() abgefangen werden: Number(null) ist 0
 * und ginge sonst als gueltiger Wert durch. Bei Koordinaten waere das der
 * Atlantik, beim Score eine glatte Null fuer „nicht bewertet" — beides falsch
 * und beides still (gleiche Falle wie in mapFeatures.toCoords).
 */
const zahlOderNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** Kuerzeste Entfernung zu einem der Bezugspunkte; null ohne verwertbare Daten. */
export const distanceToNearestProject = (
    lead: Pick<LeadPriorityInput, 'lat' | 'lng'>,
    activeProjects: ActiveProjectPoint[] | null | undefined,
): number | null => {
    const lat = zahlOderNull(lead?.lat);
    const lng = zahlOderNull(lead?.lng);
    if (lat === null || lng === null) return null;

    let best: number | null = null;
    for (const p of activeProjects || []) {
        if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lng)) continue;
        // Haversine aus nearbyProjects — bewusst wiederverwendet und NICHT
        // erneut ausgeschrieben (GlobalMapDashboard hat die Formel schon
        // einmal dupliziert, ERP-Plan 3.2 Befund 5.1).
        const km = calculateDistanceKm(lat, lng, p.lat, p.lng);
        if (best === null || km < best) best = km;
    }
    return best;
};

/**
 * Arbeitsreihenfolge eines Leads: KI-Score plus Zuschlag fuer die Naehe zum
 * naechsten aktiven Projekt.
 *
 * Warum die Naehe zaehlt: eine Begehung 20 km vom laufenden Projekt entfernt
 * kostet eine halbe Anfahrt, eine 300 km entfernte einen ganzen Tag. Der
 * Zuschlag ist gedeckelt (NEARBY_BONUS_MAX), damit er die fachliche Bewertung
 * ergaenzt statt sie zu ersetzen.
 */
export const computeLeadPriority = (
    lead: LeadPriorityInput,
    activeProjects: ActiveProjectPoint[] | null | undefined,
): LeadPriority => {
    const rawScore = zahlOderNull(lead?.score);
    const score = rawScore === null ? null : clamp100(rawScore);
    const base = score ?? NEUTRAL_SCORE;

    const distanceKm = distanceToNearestProject(lead, activeProjects);
    const step = distanceKm === null ? undefined : NEARBY_STEPS.find((s) => distanceKm <= s.maxKm);
    const naeheBonus = step?.bonus ?? 0;

    const teile: string[] = [
        score === null ? `Score ungepflegt (${NEUTRAL_SCORE} angenommen)` : `Score ${score}`,
    ];
    if (step && distanceKm !== null) {
        teile.push(`+${step.bonus} ${step.text} (${distanceKm.toFixed(0)} km)`);
    } else if (distanceKm !== null) {
        teile.push(`kein Naehe-Bonus (${distanceKm.toFixed(0)} km zum naechsten Projekt)`);
    } else {
        teile.push('nicht verortet — kein Naehe-Bonus');
    }

    return {
        priority: Math.round(clamp100(base + naeheBonus)),
        score,
        distanceKm,
        naeheBonus,
        begruendung: teile.join(', '),
    };
};

// ─── Erstkontakt: rechtlich zulaessiger Kanal ────────────────────────────────

/** Was die Kanalwahl vom Lead braucht. */
export interface ErstkontaktInput {
    email?: string | null;
    phone?: string | null;
    /** Kontrolliertes Vokabular (branchen.ts). Gepflegt = belegtes B2B-Umfeld. */
    branche?: string | null;
    /** Werbewiderspruch/Konvertierung schliessen den Erstkontakt aus. */
    status?: LeadStatus;
}

export type ErstkontaktKanal = 'brief' | 'telefon' | 'keiner';

export interface ErstkontaktHinweis {
    kanal: ErstkontaktKanal;
    /** Anzeigetext des Kanals. */
    label: string;
    /** Warum genau dieser Kanal — steht so in der Oberflaeche. */
    begruendung: string;
}

/**
 * Welcher Erstkontakt ist rechtlich zulaessig? (Plan 5, „Rechtlicher Rahmen")
 *
 * Die harte Regel: E-MAIL-KALTAKQUISE IST OHNE EINWILLIGUNG UNZULAESSIG
 * (§ 7 Abs. 2 UWG). Die Funktion gibt 'email' deshalb NIE zurueck — auch dann
 * nicht, wenn eine Adresse im Impressum stand. Telefonwerbung gegenueber
 * Unternehmen ist bei mutmasslicher Einwilligung zulaessig (§ 7 Abs. 2 Nr. 1
 * UWG); die nehmen wir nur dort an, wo der Lead als Unternehmen einer
 * einschlaegigen Branche gefuehrt wird UND eine Nummer vorliegt. Sonst bleibt
 * der Brief — der ist ohne Einwilligung zulaessig.
 *
 * Bewusst KEINE Rechtsberatung, sondern ein Leitplanken-Hinweis: die Funktion
 * verhindert den haeufigsten Fehler (Serienmail an gescrapte Adressen).
 */
export const zulaessigerErstkontakt = (lead: ErstkontaktInput): ErstkontaktHinweis => {
    if (lead?.status === 'gesperrt') {
        return {
            kanal: 'keiner',
            label: 'Kein Kontakt',
            begruendung:
                'Werbewiderspruch hinterlegt (Status „Gesperrt“) — dieser Lead darf nicht mehr beworben werden.',
        };
    }

    const phone = (lead?.phone || '').trim();
    const branche = (lead?.branche || '').trim();

    if (phone && branche) {
        return {
            kanal: 'telefon',
            label: 'Telefon',
            begruendung:
                'B2B-Telefonakquise ist bei mutmaßlicher Einwilligung zulässig (§ 7 Abs. 2 Nr. 1 UWG) — ' +
                'Branche gepflegt, Rufnummer vorhanden. E-Mail-Kaltakquise bleibt unzulässig.',
        };
    }

    if (phone) {
        return {
            kanal: 'brief',
            label: 'Brief',
            begruendung:
                'Branche ungepflegt — ohne belegtes B2B-Umfeld ist die mutmaßliche Einwilligung für einen ' +
                'Anruf nicht begründbar (§ 7 Abs. 2 Nr. 1 UWG). Der Brief ist ohne Einwilligung zulässig.',
        };
    }

    return {
        kanal: 'brief',
        label: 'Brief',
        begruendung:
            'Keine Rufnummer hinterlegt. Postalische Werbung ist ohne Einwilligung zulässig; ' +
            'E-Mail-Kaltakquise ist es nicht (§ 7 Abs. 2 UWG).',
    };
};

// ─── Lead -> Kunde ───────────────────────────────────────────────────────────

/** Die Stammdaten, mit denen die Kundenanlage vorbefuellt startet. */
export interface CustomerDraftFromLead {
    name: string;
    customerType: 'company';
    street?: string;
    zip?: string;
    city?: string;
    email?: string;
    phone?: string;
    website?: string;
    branche?: string;
    lat?: number;
    lng?: number;
    notes?: string;
}

const trimmed = (v?: string | null): string | undefined => {
    const s = (v || '').trim();
    return s || undefined;
};

/**
 * Lead -> Vorbelegung der Kundenanlage. Pur, damit die Uebernahme testbar ist
 * (welche Felder wandern mit, welche nicht).
 *
 * Bewusst NICHT uebernommen: `score`/`score_begruendung` (eine Akquise-
 * Bewertung ist am Bestandskunden bedeutungslos und waere dort irrefuehrend)
 * und `ansprechpartner` als Freitext — der gehoert in die Kontaktliste des
 * Kunden, nicht in ein Stammdatenfeld. Er wird stattdessen den Notizen
 * angehaengt, damit er nicht verloren geht.
 */
export const leadToCustomerDraft = (lead: {
    name?: string | null;
    street?: string | null;
    zip?: string | null;
    city?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    branche?: string | null;
    lat?: number | null;
    lng?: number | null;
    notes?: string | null;
    ansprechpartner?: string | null;
    quelle?: string | null;
}): CustomerDraftFromLead => {
    const notizen = [
        trimmed(lead?.notes),
        lead?.ansprechpartner ? `Ansprechpartner (aus Lead): ${lead.ansprechpartner.trim()}` : undefined,
        lead?.quelle ? `Herkunft: ${lead.quelle}` : undefined,
    ].filter(Boolean);

    const lat = zahlOderNull(lead?.lat);
    const lng = zahlOderNull(lead?.lng);

    return {
        name: (lead?.name || '').trim(),
        customerType: 'company',
        street: trimmed(lead?.street),
        zip: trimmed(lead?.zip),
        city: trimmed(lead?.city),
        email: trimmed(lead?.email),
        phone: trimmed(lead?.phone),
        website: trimmed(lead?.website),
        branche: trimmed(lead?.branche),
        // Nur ein VOLLSTAENDIGES Paar wandert mit: eine halbe Koordinate
        // ergaebe am Kunden einen Marker im Nirgendwo.
        lat: lat !== null && lng !== null ? lat : undefined,
        lng: lat !== null && lng !== null ? lng : undefined,
        notes: notizen.length ? notizen.join('\n') : undefined,
    };
};

// ─── Wiedervorlagen ──────────────────────────────────────────────────────────

export interface WiedervorlageLike {
    faelligAm?: string | null;
    erledigt?: boolean;
}

/**
 * Ist die Wiedervorlage faellig? Vergleich rein auf dem Datumsanteil (Muster
 * quoteLogic.isQuoteExpired) — eine Uhrzeit hat eine `date`-Spalte nicht, und
 * ein UTC-Vergleich verschoebe den Stichtag nachts um einen Tag.
 */
export const istFaellig = (a: WiedervorlageLike, todayIso: string): boolean => {
    if (!a || a.erledigt) return false;
    const faellig = (a.faelligAm || '').slice(0, 10);
    if (!faellig) return false;
    return faellig <= todayIso.slice(0, 10);
};

/** Anzahl faelliger, offener Wiedervorlagen — die Zahl im Tab-Badge. */
export const zaehleFaellige = (
    activities: WiedervorlageLike[] | null | undefined,
    todayIso: string,
): number => (activities || []).filter((a) => istFaellig(a, todayIso)).length;
